<template>
  <ReportChartCard :title="chartTitle" :subtitle="sensorSubtitle" full-height is-relative>
    <div class="w-full pb-2">
      <VueApexCharts
        class="w-full bg-white"
        type="rangeBar"
        :options="mergedChartOptions"
        :series="toRaw(series)"
        :height="chartHeight"
        :key="chartKey" />
    </div>
  </ReportChartCard>
</template>

<script setup lang="ts">
import { convertToLocalTimeUnix } from '@/composable/useFormatDateAndHours'
import { useI18n } from '@/composable/useI18n'
import { useCompareDoorOpenChart } from '@/composables/useCompareDoorOpenChart'
import { useDoorOpenChart } from '@/composables/useDoorOpenChart'
import { useReportStore } from '@/store/report'
import {
  mergeCorrelatedChartOptions,
  type CorrelatedChartPair,
} from '@/utils/report/correlated-charts-layout'
import { storeToRefs } from 'pinia'
import { computed, toRaw } from 'vue'
import VueApexCharts from 'vue3-apexcharts'
import ReportChartCard from './ReportChartCard.vue'

const props = defineProps<{
  pair: CorrelatedChartPair
  showXAxisLabels: boolean
}>()

const { t } = useI18n()
const { basePeriod, comparePeriod, doorOpenReadings, compareDoorOpenReadings } = storeToRefs(useReportStore())

const isBasePair = computed(() => props.pair === 'base')
const baseChart = useDoorOpenChart()
const compareChart = useCompareDoorOpenChart()

const chartOptions = computed(() =>
  isBasePair.value ? baseChart.chartOptions.value : compareChart.chartOptions.value,
)
const series = computed(() => (isBasePair.value ? baseChart.series.value : compareChart.series.value))
const chartHeight = computed(() =>
  isBasePair.value ? baseChart.chartHeight.value : compareChart.chartHeight.value,
)

const sensorSubtitle = computed(() => {
  const readings = isBasePair.value ? doorOpenReadings.value : compareDoorOpenReadings.value
  const names = (readings || []).map((sensor) => sensor.name).filter(Boolean)
  return names.length ? names.join(' · ') : undefined
})

const period = computed(() => (isBasePair.value ? basePeriod.value : comparePeriod.value))

const timeRange = computed(() => {
  const [startDate, endDate] = period.value || []
  return {
    min: startDate ? convertToLocalTimeUnix(startDate) : undefined,
    max: endDate ? convertToLocalTimeUnix(endDate) : undefined,
  }
})

const mergedChartOptions = computed(() =>
  mergeCorrelatedChartOptions(chartOptions.value as Record<string, unknown>, {
    showXAxisLabels: props.showXAxisLabels,
    xAxisMin: timeRange.value.min,
    xAxisMax: timeRange.value.max,
    hideCategoryAxisLabels: true,
  }),
)

const chartTitle = computed(() =>
  isBasePair.value ? t('report.door_open.title') : t('report.door_open.title_compare'),
)

const chartKey = computed(() => `${props.pair}-door-${chartHeight.value}`)
</script>
